import React from 'react';
import type { ToolFilterCondition } from '@troia-v3/types';

import { Input, Select } from '@/shared/components/ui';
import { Icon } from '@/shared/icons';

import {
  isJsonPathField,
  JSON_FIELD_SENTINEL,
  WEBHOOK_FIELD_GROUPS,
  WEBHOOK_FILTER_OPERATORS,
  WEBHOOK_OPERATORS_WITHOUT_VALUE,
} from './WebhookFilterDefinitions';

interface WebhookFilterRowProps {
  filter: ToolFilterCondition;
  index: number;
  onChange: (filter: ToolFilterCondition) => void;
  onRemove: () => void;
  disabled?: boolean;
}

export const WebhookFilterRow: React.FC<WebhookFilterRowProps> = ({
  filter,
  index,
  onChange,
  onRemove,
  disabled,
}) => {
  const isJsonField = isJsonPathField(filter.field);
  const selectValue = isJsonField ? JSON_FIELD_SENTINEL : filter.field;
  const hideValue = WEBHOOK_OPERATORS_WITHOUT_VALUE.includes(filter.operator);

  const handleFieldSelect = (value: string): void => {
    if (value === JSON_FIELD_SENTINEL) {
      onChange({ ...filter, field: '$.' });
    } else {
      onChange({ ...filter, field: value });
    }
  };

  return (
    <div
      className="p-4 rounded-lg border"
      style={{ borderColor: 'var(--theme-card-border)' }}
      data-testid={`webhook-filter-row-${index}`}
    >
      <div className="grid grid-cols-1 md:grid-cols-12 gap-3">
        {/* Campo */}
        <div className="md:col-span-4">
          <Select
            label="Campo"
            value={selectValue}
            onChange={handleFieldSelect}
            groups={WEBHOOK_FIELD_GROUPS}
            placeholder="Selecionar campo..."
            disabled={disabled}
            data-testid={`webhook-filter-field-${index}`}
          />
          {isJsonField && (
            <div className="mt-2">
              <Input
                type="text"
                value={filter.field}
                onChange={(e) => onChange({ ...filter, field: e.target.value })}
                placeholder="$.cliente.status"
                disabled={disabled}
                data-testid={`webhook-filter-json-path-${index}`}
              />
              <p className="text-xs mt-1" style={{ color: 'var(--theme-muted-text)' }}>
                Caminho no JSON recebido. Ex: <code>$.status</code> ou <code>$.cliente.status</code>
              </p>
            </div>
          )}
        </div>

        {/* Operador */}
        <div className="md:col-span-3">
          <Select
            label="Operador"
            value={filter.operator}
            onChange={(value) => onChange({ ...filter, operator: value })}
            options={WEBHOOK_FILTER_OPERATORS}
            disabled={disabled}
            data-testid={`webhook-filter-operator-${index}`}
          />
        </div>

        {/* Valor */}
        <div className="md:col-span-4">
          <label className="block text-xs font-medium mb-1" style={{ color: 'var(--theme-muted-text)' }}>
            Valor
          </label>
          {hideValue ? (
            <div
              className="w-full px-3 py-2 rounded-lg border text-sm"
              style={{
                backgroundColor: 'var(--theme-input-background)',
                borderColor: 'var(--theme-input-border)',
                color: 'var(--theme-muted-text)',
                opacity: 0.6,
              }}
            >
              &mdash;
            </div>
          ) : (
            <Input
              type="text"
              value={filter.value}
              onChange={(e) => onChange({ ...filter, value: e.target.value })}
              placeholder="Valor para comparação"
              disabled={disabled}
              data-testid={`webhook-filter-value-${index}`}
            />
          )}
        </div>

        {/* Remover */}
        <div className="md:col-span-1 flex items-end justify-end pb-1">
          <button
            type="button"
            onClick={onRemove}
            disabled={disabled}
            className="p-2 rounded transition-colors"
            style={{ color: 'var(--theme-error-color)' }}
            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'var(--theme-error-background)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
            data-testid={`webhook-filter-remove-${index}`}
          >
            <Icon name="trash" className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

WebhookFilterRow.displayName = 'WebhookFilterRow';
